import { BehaviorSubject, Observable, of, combineLatest, throwError } from 'rxjs';
import { Request } from './request.model';
import { RequestService } from './request.service';
import { Injectable } from '@angular/core';
import { ModelStore } from '../../../shared/stores/model.store';
import * as _ from 'lodash';
import { map, switchMap, take, catchError, tap } from 'rxjs/operators';

interface BorrowerRequestsMap {
    [borrowerId: number]: number[];
}

@Injectable()
export class RequestStore extends ModelStore<Request> {

    private borrowerRequestIds$ = new BehaviorSubject<BorrowerRequestsMap>({});
    private borrowerRequestRequests: number[] = [];

    constructor(private requestService: RequestService) {
        super(requestService);
    }

    createMany(requests: Request[], borrowerId: number): Observable<Request[]> {
        return this.requestService.createMany(requests, borrowerId).pipe(
            tap((savedRequests: Request[]) => {
                _.each(savedRequests, (request) => this.inject(request));
            })
        );
    }

    updateMany(requests: Request[], borrowerId: number): Observable<Request[]> {
        return this.requestService.updateMany(requests, borrowerId).pipe(
            tap((updatedRequests) => {
                _.each(updatedRequests, (request) => this.inject(request));
            })
        );
    }

    delete(model: Request) {
        return super.delete(model);
    }

    forBorrower(borrowerId: number): Observable<Request[]> {
        return this.requestIdsForBorrower(borrowerId).pipe(
            switchMap((requestIds) => {
                const requestObservables = _.map(requestIds, (requestId) => this.get(requestId));
                if (!requestObservables.length) {
                    return of([]);
                }
                return combineLatest([...requestObservables]).pipe(
                    map(requests => requests.filter(request => request !== null) as Request[])
                );
            })
        );
    }

    ejectForBorrower(borrowerId: number): Observable<void> {
        this.borrowerRequestRequests = this.borrowerRequestRequests.filter(x => x !== borrowerId);
        return this.borrowerRequestIds$.pipe(
            take(1),
            map((borrowerRequestIds: BorrowerRequestsMap) => {
                delete borrowerRequestIds[borrowerId];
                this.borrowerRequestIds$.next(borrowerRequestIds);
            })
        );
    }

    protected requestIdsForBorrower(borrowerId: number): Observable<number[]> {
        return this.borrowerRequestIds$.pipe(
            switchMap((borrowerRequestIds: BorrowerRequestsMap) => {
                if (this.borrowerRequestRequests.includes(borrowerId)) {
                    return of(borrowerRequestIds[borrowerId]);
                }
                this.borrowerRequestRequests.push(borrowerId);
                return this.requestService.forBorrower(borrowerId).pipe(
                    catchError((err) => {
                        this.borrowerRequestRequests.splice(this.borrowerRequestRequests.indexOf(borrowerId), 1);
                        return throwError(() => err);
                    }),
                    map((requests: Request[]) => {
                        _.each(requests, (request) => this.inject(request));
                        return _.map(requests, (request) => request.id);
                    })
                );
            })
        );
    }

    protected afterInject(model) {
        this.borrowerRequestIds$.pipe(take(1)).subscribe((borrowerRequestIds: BorrowerRequestsMap) => {
            const existingList = borrowerRequestIds[model.borrowerId];
            if (!existingList) {
                borrowerRequestIds[model.borrowerId] = [model.id];
                return this.borrowerRequestIds$.next(borrowerRequestIds);
            }
            if (!existingList.includes(model.id)) {
                existingList.push(model.id);
            }
            this.borrowerRequestIds$.next(borrowerRequestIds);
        });
    }
}
